import { FC, ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { StatusModal } from 'src/components/StatusModal'
import { AI_LISTENING_PROMPT_DETAILS_ROUTE } from 'src/constants/routes'
import { useAiListeningLink } from 'src/hooks/useAiListeningDateRange'
import { useDeleteAiListeningPromptMutation } from 'src/reactQuery/useDeleteAiListeningPromptMutation'
import { useUpdateAiListeningPromptStatusMutation } from 'src/reactQuery/useUpdateAiListeningPromptStatusMutation'
import { PromptListItem } from 'src/services/aiListeningApi/brandProfile.types'
import { PromptCard } from './PromptCard'
import styles from './promptList.module.scss'
import { StateCard } from '../StateCard'

interface PromptListProps {
  // Replaces the default empty block (e.g. "no prompts match the filter").
  emptyState?: ReactNode
  isLoading?: boolean
  onAddPrompt: () => void
  prompts: PromptListItem[]
}

// "Tracking N Prompts": the prompt cards with their ⋮ actions, the delete
// confirmation, and Add New Prompt.
export const PromptList: FC<PromptListProps> = ({
  emptyState,
  isLoading = false,
  onAddPrompt,
  prompts,
}) => {
  const navigate = useNavigate()
  const link = useAiListeningLink()
  const [promptToDelete, setPromptToDelete] = useState<PromptListItem | null>(
    null
  )

  const statusMutation = useUpdateAiListeningPromptStatusMutation()
  const deleteMutation = useDeleteAiListeningPromptMutation({
    onSuccess: () => setPromptToDelete(null),
  })

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>
            Tracking {prompts.length}{' '}
            {prompts.length === 1 ? 'Prompt' : 'Prompts'}
          </h2>
          <p className={styles.subtitle}>
            Click the prompt to view its deep analysis
          </p>
        </div>
        <button
          className={styles.addButton}
          onClick={onAddPrompt}
          type="button"
        >
          <svg fill="none" height="20" viewBox="0 0 20 20" width="20">
            <circle
              cx="10"
              cy="10"
              r="7.5"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M10 6.5v7M6.5 10h7"
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth="1.5"
            />
          </svg>
          Add New Prompt
        </button>
      </div>

      {!isLoading && prompts.length === 0 && (
        <div className={styles.empty}>
          {emptyState ?? (
            <StateCard
              description="Prompts are the questions your customers ask AI assistants. Add one to see how ChatGPT, Claude, Gemini and Perplexity answer it."
              icon="prompt"
              inline
              title="No prompts yet"
            />
          )}
        </div>
      )}

      {prompts.map((prompt) => (
        <PromptCard
          key={prompt._id}
          onDelete={() => setPromptToDelete(prompt)}
          onOpen={() =>
            navigate(
              link(AI_LISTENING_PROMPT_DETAILS_ROUTE, { promptId: prompt._id })
            )
          }
          onStatusChange={(status) =>
            statusMutation.mutate({ promptId: prompt._id, status })
          }
          prompt={prompt}
        />
      ))}

      <StatusModal
        doneLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        hideCountdown
        isOpen={!!promptToDelete}
        message="This prompt will be removed from tracking and all future comparisons. Historical data collected from this prompt may not be recoverable."
        onClose={() => setPromptToDelete(null)}
        onSecondaryAction={() =>
          promptToDelete &&
          !deleteMutation.isPending &&
          deleteMutation.mutate(promptToDelete._id)
        }
        secondaryLabel="Cancel"
        title="Delete Prompt?"
        type="delete"
      />
    </section>
  )
}
